import { FulfillmentTypes } from '@moda/portal-stanchions';
import { SizeInventoryFragment } from '../../../generated/types';
import { GetDeliveryEstimateQuery } from './deliveryEstimate.rest';

export const SAME_DAY_METHOD_ID = 3;
export const RANGE_COPY = 'Ships in 5 to 7 days';

const MS_PER_MINUTE = 60_000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const STALE_DATE_DAYS = 30;
const MS_PER_HOUR = MS_PER_MINUTE * MINUTES_PER_HOUR;
const MS_PER_DAY = MS_PER_HOUR * HOURS_PER_DAY;
const STALE_DATE_MS = STALE_DATE_DAYS * MS_PER_DAY;

const FULFILMENT_TYPES_WITH_OWN_MESSAGE: (number | null)[] = [
  FulfillmentTypes.DropShip,
  FulfillmentTypes.MiraklDropship
];

const FULFILMENT_TYPES_WITHOUT_DATES: (number | null)[] = [FulfillmentTypes.MiraklEconcession];

export const hasOwnDeliveryMessage = (fulfilmentType: number | null) =>
  FULFILMENT_TYPES_WITH_OWN_MESSAGE.includes(fulfilmentType);

type Estimate = GetDeliveryEstimateQuery['deliveryEstimate'];

export interface DeliveryOption {
  id: number;
  label: string;
  charges: number;
  date: string;
  arrivesAt: number;
  sameDay: boolean;
}

export type DeliveryView =
  | { kind: 'hidden' }
  | { kind: 'needs-zip' }
  | { kind: 'range' }
  | { kind: 'size-ships-later'; shipsBy: string | null }
  | {
      kind: 'options';
      options: DeliveryOption[];
      cutoffAt: string | null;
    };

const shortLabel = (name: string) =>
  name.replace(/\s+(Shipping|Delivery)$/i, '').replace(/-/g, ' ');

const shortDate = (arrivalDate: string) => arrivalDate.replace(/^(\w{3})\w*/, '$1');

const withoutWeekday = (date: string) => date.replace(/^\w+,\s*/, '');

const arrivalTime = (arrivalDate: string, now: Date) => {
  const date = new Date(`${withoutWeekday(arrivalDate)} ${now.getFullYear()}`);
  if (date.getTime() < now.getTime() - STALE_DATE_MS) date.setFullYear(now.getFullYear() + 1);
  return date.getTime();
};

const sizeShipsNow = (size: SizeInventoryFragment) => (size.available ?? 0) > (size.onOrder ?? 0);

const sameDayOpen = (estimate: Estimate, now: Date) => {
  const cutoffAt = estimate.sameDay?.cutoffAt;
  return Boolean(estimate.sameDay?.available && cutoffAt && new Date(cutoffAt) > now);
};

export const buildDeliveryView = ({
  estimate,
  fulfilmentType,
  size,
  hasLocation,
  now
}: {
  estimate: Estimate | undefined;
  fulfilmentType: number | null;
  size: SizeInventoryFragment | null;
  hasLocation: boolean;
  now: Date;
}): DeliveryView => {
  if (hasOwnDeliveryMessage(fulfilmentType)) return { kind: 'hidden' };
  if (FULFILMENT_TYPES_WITHOUT_DATES.includes(fulfilmentType)) return { kind: 'range' };

  if (size && !sizeShipsNow(size)) {
    return { kind: 'size-ships-later', shipsBy: size.processDeliveryDates?.deliveryEndsOn ?? null };
  }

  if (!hasLocation) return { kind: 'needs-zip' };
  if (!estimate) return { kind: 'hidden' };

  const sameDay = sameDayOpen(estimate, now);

  const options = (estimate.shippingMethods ?? [])
    .filter(method => method.id !== SAME_DAY_METHOD_ID || sameDay)
    .flatMap(method => {
      if (!method.arrivalDate) return [];
      const isSameDay = method.id === SAME_DAY_METHOD_ID;
      return [
        {
          id: method.id,
          label: shortLabel(method.name),
          charges: method.charges,
          date: shortDate(method.arrivalDate),
          arrivesAt: isSameDay ? now.getTime() : arrivalTime(method.arrivalDate, now),
          sameDay: isSameDay
        }
      ];
    })
    .sort((left, right) => left.arrivesAt - right.arrivesAt || left.charges - right.charges);

  if (options.length === 0) return { kind: 'range' };

  return {
    kind: 'options',
    options,
    cutoffAt: sameDay ? (estimate.sameDay?.cutoffAt ?? null) : null
  };
};

const startOfDay = (time: number) => new Date(time).setHours(0, 0, 0, 0);

export const formatSummaryDate = (option: DeliveryOption, now: Date) => {
  const days = Math.round((startOfDay(option.arrivesAt) - startOfDay(now.getTime())) / MS_PER_DAY);
  if (option.sameDay || days === 0) return `today, ${withoutWeekday(option.date)}`;
  if (days === 1) return `tomorrow, ${withoutWeekday(option.date)}`;
  return option.date;
};

export const formatCountdown = (msLeft: number) => {
  const totalMinutes = Math.max(1, Math.floor(msLeft / MS_PER_MINUTE));
  const hours = Math.floor(totalMinutes / MINUTES_PER_HOUR);
  if (hours === 0) return `${totalMinutes} min`;
  return hours === 1 ? '1 hour' : `${hours} hours`;
};

export const formatCharges = (charges: number, currency: string) => {
  if (charges === 0) return 'Free';
  const fractionDigits = Number.isInteger(charges) ? 0 : 2;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(charges);
};

const ZIP_PLUS_FOUR = /^(\d{5})-?\d{4}$/;
const ZIP_DIGITS = 5;

export const normalizeZip = (value: string) => {
  const zipPlusFour = value.trim().match(ZIP_PLUS_FOUR);
  if (zipPlusFour) return zipPlusFour[1];
  return value.replace(/\D/g, '').slice(0, ZIP_DIGITS);
};

export const isCompleteZip = (value: string) => /^\d{5}$/.test(value);
